import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AppHeader } from '@/components/AppHeader';
import { ProjectHeader } from '@/components/ProjectHeader';
import { FileText, HelpCircle, Database, ChevronRight, FolderOpen, Search, BookOpen, UserPlus, X, Users, ChevronDown, Settings } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { getProjectWithRfi, updateProject } from '@/lib/api/projects';
import { toast } from '@/hooks/use-toast';
import type { ProjectWithRFIResponse, ProjectMember, ProjectMemberRole } from '@/types/backend';
import { CreateProjectDialog } from '@/components/CreateProjectDialog';
import { useProjectRole } from '@/hooks/useProjectRole';
import type { TeamMember } from '@/components/TeamAvatars';

interface ProjectFolder {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  route: string | ((projectData: ProjectWithRFIResponse | undefined) => string);
}

const projectFolders: ProjectFolder[] = [
  {
    id: 'rfi',
    label: 'RFI',
    icon: FileText,
    description: 'Generate and track Request for Information',
    route: (projectData) => import.meta.env?.VITE_DEMO_MODE === 'true'
      ? '/rfi'
      : projectData?.rfi ? '/rfi-tracker' : '/rfi',
  },
  {
    id: 'deep-research',
    label: 'Deep Research',
    icon: Search,
    description: 'Target company research and analysis',
    route: '/deep-research',
  },
  {
    id: 'databook-builder',
    label: 'Databook Builder',
    icon: BookOpen,
    description: 'Staged flow: upload, entities, review, download, build',
    route: '/databook-builder',
  },
];

function deriveInitials(email: string): string {
  return email.split('@')[0].split('.').map(s => s[0]?.toUpperCase()).filter(Boolean).join('').slice(0, 2) || email.slice(0, 2).toUpperCase();
}

function toTeamMember(m: ProjectMember): TeamMember {
  return { email: m.email, role: m.role, initials: deriveInitials(m.email) };
}

const ProjectDetail = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [editProjectOpen, setEditProjectOpen] = useState(false);

  // Fetch project data from backend
  const { data: projectData, isLoading, refetch: refetchProject } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectId ? getProjectWithRfi(projectId) : Promise.reject('No project ID'),
    enabled: !!projectId,
  });

  const { canEdit, canManageMembers } = useProjectRole(projectId);

  const teamMembers = useMemo<TeamMember[]>(
    () => (projectData?.members ?? []).map(toTeamMember),
    [projectData?.members],
  );

  const handleMembersChange = async (next: TeamMember[]) => {
    if (!projectId) return;
    try {
      await updateProject(projectId, {
        members: next.map(m => ({ email: m.email, role: m.role })),
      });
      refetchProject();
    } catch (error) {
      console.error('Failed to update members:', error);
      // 403 handled globally; for other errors, surface message
      const msg = error instanceof Error ? error.message : 'Failed to update team members.';
      toast({ title: 'Failed to update team', description: msg, variant: 'destructive' });
    }
  };

  const project = projectData ? {
    name: projectData.name,
    client: projectData.entity_name || 'Client',
  } : {
    name: 'Project',
    client: 'Client',
  };

  const handleFolderClick = (route: string | ((projectData: ProjectWithRFIResponse | undefined) => string)) => {
    // Resolve dynamic route if it's a function
    const resolvedRoute = typeof route === 'function' ? route(projectData) : route;
    
    if (!projectId) {
      navigate(resolvedRoute);
      return;
    }
    const sep = resolvedRoute.includes("?") ? "&" : "?";
    navigate(`${resolvedRoute}${sep}projectId=${encodeURIComponent(projectId)}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <ProjectHeader
        backTo="/"
        title={project.name}
        teamMembers={teamMembers}
        onMembersChange={handleMembersChange}
        canManageTeam={canManageMembers}
      />

      <main style={{ maxWidth: 960, margin: '0 auto', padding: '40px 40px 100px' }}>
          {/* Page header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
            <h1 style={{ fontSize: 28, fontWeight: 400, letterSpacing: '-0.02em', fontFamily: "'DM Serif Display', Georgia, serif", margin: 0 }}>
              {project.name}
            </h1>
            {canEdit && (
              <button
                type="button"
                onClick={() => setEditProjectOpen(true)}
                disabled={!projectData}
                aria-label="Project settings"
                title="Project settings"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: '1px solid #E8E6E1',
                  background: '#FFFFFF',
                  color: '#5A5A54',
                  cursor: projectData ? 'pointer' : 'not-allowed',
                  opacity: projectData ? 1 : 0.5,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { if (projectData) { e.currentTarget.style.borderColor = '#B5B5AF'; e.currentTarget.style.background = '#FAF9F6'; } }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E8E6E1'; e.currentTarget.style.background = '#FFFFFF'; }}
              >
                <Settings style={{ width: 16, height: 16 }} />
              </button>
            )}
          </div>

          {projectData && (
            <CreateProjectDialog
              open={editProjectOpen}
              onOpenChange={setEditProjectOpen}
              project={projectData}
              onSuccess={() => refetchProject()}
            />
          )}

          {/* Folders grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {projectFolders.map((folder) => {
              const IconComponent = folder.icon;

              return (
                <div
                  key={folder.id}
                  onClick={() => handleFolderClick(folder.route)}
                  className="group transition-all cursor-pointer"
                  style={{ background: '#FFFFFF', border: '1px solid #E8E6E1', borderRadius: 14, padding: 24, display: 'flex', alignItems: 'center', gap: 18 }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#B5B5AF'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#E8E6E1'; }}
                >
                  <div style={{ width: 48, height: 48, background: '#FAF9F6', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <IconComponent style={{ width: 22, height: 22, color: '#8A8A84' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <span style={{ fontSize: 15, fontWeight: 600 }}>{folder.label}</span>
                    </div>
                    <span style={{ fontSize: 13, color: '#8A8A84' }}>{folder.description}</span>
                  </div>
                  <span style={{ color: '#B5B5AF', fontSize: 16 }}>›</span>
                </div>
              );
            })}
          </div>
      </main>
    </div>
  );
};

export default ProjectDetail;
